import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader, EmptyState } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Zap, Save, Loader2, Users } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const B2CEarlyCod = ({ user, onLogout }) => {
  const canManage = can(user, "b2c.early_cod:manage");
  const [cfg, setCfg] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/b2c/admin/early-cod/config`),
        axios.get(`${API}/b2c/admin/early-cod/sellers`).catch(() => ({ data: { sellers: [] } })),
      ]);
      setCfg(c.data);
      setSellers(s.data?.sellers || []);
    } catch (e) {
      toast.error(errDetail(e, "Failed to load Early COD config"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setPlan = (i, field, value) =>
    setCfg((c) => ({ ...c, plans: c.plans.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        gst_pct: Number(cfg.gst_pct),
        plans: cfg.plans.map((p) => ({
          tier: p.tier, days: Number(p.days), rate_pct: Number(p.rate_pct), enabled: !!p.enabled,
        })),
      };
      const { data } = await axios.put(`${API}/b2c/admin/early-cod/config`, payload);
      setCfg(data);
      toast.success("Early COD settings saved");
    } catch (e) {
      toast.error(errDetail(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full max-w-4xl">
        <PageHeader
          title="Early COD"
          subtitle="Configure the early COD-remittance plans (D+N) sellers can opt into"
        />

        {loading || !cfg ? (
          <div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-300" /></div>
        ) : (
          <>
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Zap className="w-5 h-5 text-brand" /> Configuration</CardTitle>
                <CardDescription>The fee on each plan is a % of COD; GST is added on top and deducted from the seller's COD payout (net to bank).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex flex-wrap items-end gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300" data-testid="early-cod-enabled" />
                    <span className="text-sm font-medium text-slate-700">Early COD enabled (sellers can opt in)</span>
                    <Badge className={`border-0 ${cfg.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                      {cfg.enabled ? "On" : "Off"}
                    </Badge>
                  </label>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">GST % (added on the fee)</Label>
                    <Input type="number" step="0.01" min="0" max="100" className="w-28"
                      value={cfg.gst_pct} onChange={(e) => setCfg({ ...cfg, gst_pct: e.target.value })} data-testid="early-cod-gst" />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-left">
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Tier</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Days after delivery</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Fee (% of COD)</th>
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Enabled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cfg.plans.map((p, i) => (
                        <tr key={p.tier} className="border-b border-slate-50">
                          <td className="px-3 py-2 font-semibold text-slate-800">{p.tier}</td>
                          <td className="px-3 py-2">
                            <Input type="number" min="1" max="60" className="w-24" value={p.days}
                              onChange={(e) => setPlan(i, "days", e.target.value)} data-testid={`early-cod-days-${p.tier}`} />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <Input type="number" step="0.01" min="0" max="100" className="w-24" value={p.rate_pct}
                                onChange={(e) => setPlan(i, "rate_pct", e.target.value)} data-testid={`early-cod-rate-${p.tier}`} />
                              <span className="text-slate-400">%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={!!p.enabled} onChange={(e) => setPlan(i, "enabled", e.target.checked)}
                              className="h-4 w-4 rounded border-slate-300" data-testid={`early-cod-plan-enabled-${p.tier}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end">
                  <Button onClick={save} disabled={!canManage || saving} data-testid="early-cod-save">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-brand" /> Enrolled sellers ({sellers.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {sellers.length === 0 ? (
                  <EmptyState icon={Users} title="No sellers enrolled yet" description="KYC-approved sellers can opt into a plan once Early COD is enabled." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 text-left">
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">Seller</th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">Tier</th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">KYC</th>
                          <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">Opted at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellers.map((s) => (
                          <tr key={s.seller_id} className="border-b border-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-800">{s.brand_name}</td>
                            <td className="px-4 py-3"><Badge className="bg-brand-bg text-brand border-0">{s.tier}</Badge></td>
                            <td className="px-4 py-3 text-slate-600">{s.kyc_status || "—"}</td>
                            <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{s.opted_at ? formatToIST(s.opted_at) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default B2CEarlyCod;
